import { Component, DoCheck, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PaaService } from '../paa.service';
import { PaaRepository } from '@/store/paa-state/paa-state.repository';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '../../../components/form-builder/form-builder.component';
import { ButtonModule } from 'primeng/button';
import { DynamicForm, FileRepresentation, SelectOption } from '@/components/form-builder/form-builder.types';
import { getPaaUpdatePlanFormObject } from './utils/paa-update-form-object';
import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { BlockBuilderComponent } from '../../../components/block-builder/block-builder.component';
import { PaaWorkflowBlocksService } from '../paa-workflow-blocks.service';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { PaaMilestonesService } from '../paa-milestones.service';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { DialogObject } from '@/components/dialog/dialog.types';
import { getPaaDeleteObject } from './utils/paa-delete-dialog-object';
import { url } from '@/utils/url.utils';
import { BehaviorSubject, catchError, combineLatest, of } from 'rxjs';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { ContractingAuthorityService } from '@/pages/settings/contracting-authority/contracting-authority.service';
import { paaPreDraftSchema } from '@/validations/paa/paa-predraft.validation';
import { StatusComponent } from '../../../components/shared/status/status.component';
import { TranslateModule } from '@ngx-translate/core';
import { CurrentTasksService } from '@/pages/current-tasks/current-tasks.service';
import { PaaDetails, TasksStatus } from '@/types';
import { SpinnerComponent } from '../../../components/shared/spinner/spinner.component';
import { string } from '@/utils/string.utils';
import { paaMilestonesStoreFactory } from '@/store/paa-state/milestones-state/milestones.store';
import { fileUtils } from '@/utils/file.utils';
import { getPAAUploadDialog } from './utils/paa-upload.dialog-object';
import { getPAADownloadDialog } from '@/pages/paa/edit-plan/utils/paa-download.dialog-object';
import _ from 'lodash';
import { getPaaConfirmWorkflowObject } from './utils/paa-confirm-workflow.dialog-object';

@Component({
  selector: 'sm-paa-current-plan',
  templateUrl: './edit-plan.component.html',
  styleUrl: './edit-plan.component.scss',
  imports: [
    CommonModule,
    PageHeaderComponent,
    FormBuilderComponent,
    ButtonModule,
    BlockBuilderComponent,
    CommonModule,
    DialogComponent,
    StatusComponent,
    TranslateModule,
    SpinnerComponent,
  ],
})
export class PaaEditPlanComponent implements OnInit, OnDestroy, DoCheck {
  //services
  paaService = inject(PaaService);
  paaMilestonesService = inject(PaaMilestonesService);
  paaWorkflowBlocksService = inject(PaaWorkflowBlocksService);
  paaRepository = inject(PaaRepository);
  multiFormAccordionService = inject(MultiFormAccordionService);
  contractingAuthorityService = inject(ContractingAuthorityService);
  messageService = inject(MessageService);
  fileService = inject(FileService);
  currentTasksService = inject(CurrentTasksService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);

  id = this.activatedRoute.snapshot.params['id'] as number;

  //breadcrumb
  breadcrumb = [
    {
      label: 'paa.title',
      routerLink: ['/paa-current-plans'],
    },
    {
      label: '',
      routerLink: ['paa-edit-plan', this.id.toString()],
    },
  ];

  @ViewChild('dialog') dialogComponent!: DialogComponent;
  paaDialogObject?: DialogObject;

  loading$ = new BehaviorSubject<boolean>(true);
  isUpdatable$ = new BehaviorSubject<boolean>(true);

  acOptions: SelectOption[] = [];
  formObject: DynamicForm = getFormBuilderObjectFactory();
  controlBlocks: DynamicBlocks[] = [];
  paaDetails: PaaDetails | null = null;
  status = '';
  comment = new BehaviorSubject<string>('');
  commentError = new BehaviorSubject<string | null>(null);

  isAltered = new BehaviorSubject<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor() {}

  ngOnInit() {
    this.loadData();
  }

  ngDoCheck() {
    const milestoneAltered = this.paaMilestonesService.altered();

    const dto = this.paaRepository.get('dto');
    const initialDto = this.paaRepository.get('initialDto');
    const planAltered = !_.isEqual(dto, initialDto);

    this.isAltered.next(milestoneAltered || planAltered);
  }

  ngOnDestroy() {
    this.multiFormAccordionService.reset(0);
    this.paaRepository.reset();
  }

  onCommentChange(value: string) {
    this.comment.next(value);
    if (this.commentError.getValue()) {
      this.commentError.next(null);
    }
  }

  loadData() {
    this.loading$.next(true);
    this.multiFormAccordionService.formFactory = (id: string) => paaMilestonesStoreFactory(id);
    this.paaMilestonesService.initialize();

    //set ac options
    combineLatest({
      acWithNoPaa: this.paaService.getAcWithNoPaa().pipe(catchError(() => of([]))),
      allAc: this.contractingAuthorityService.getCurrentUserContractingAuthority().pipe(catchError(() => of([]))),
      paaDetails: this.paaService.getPaaDetails(this.id),
    }).subscribe(({ acWithNoPaa, allAc, paaDetails }) => {
      this.multiFormAccordionService.formFactory = (id: string) => paaMilestonesStoreFactory(id);
      this.paaMilestonesService.setMilestoneDtos(paaDetails.paa.realisations || []);
      this.paaRepository.setResponseAndDto(paaDetails.paa);
      this.breadcrumb[1].label = `Plan Actual - ${paaDetails.paa.ref}`;
      this.status = string.screamingSnakeToCapitalized(paaDetails.paa.status);
      this.paaDetails = paaDetails;
      if (paaDetails.paa.status === TasksStatus.BROUILLON) this.isUpdatable$.next(true);
      else if ([TasksStatus.PUBLIEE].includes(paaDetails.paa.status as TasksStatus) || !paaDetails.isUpdatable) this.isUpdatable$.next(false);
      if (paaDetails.paa.status !== TasksStatus.PUBLIEE)
        this.controlBlocks =
          !paaDetails.paa.processInstanceId && this.status !== TasksStatus.PUBLIEE
            ? this.paaWorkflowBlocksService.getDraftPaaBlocks({
                refreshCount: paaDetails.paa.nbrActualisation,
                save: () => this.updatePaa('draft'),
                rest: () => this.reset(),
                validate: () => {
                  if (this.isAltered.getValue()) {
                    this.paaDialogObject = getPaaConfirmWorkflowObject({
                      action: () => {
                        this.updatePaa('draft', () => this.startWorkflow());
                      },
                      toggleVisiblity: () => this.dialogComponent.toggleVisible(),
                    });
                    this.dialogComponent.toggleVisible();
                  } else {
                    this.startWorkflow();
                  }
                },
                isAltered: this.isAltered,
                remove: () => {
                  this.paaDialogObject = getPaaDeleteObject({
                    store: this.paaRepository,
                    action: () => this.removePaa(),
                    toggleVisiblity: () => this.dialogComponent.toggleVisible(),
                  });
                  this.dialogComponent.toggleVisible();
                },
              })
            : this.paaWorkflowBlocksService.getFlowablePaaBlocks({
                refreshCount: paaDetails.paa.nbrActualisation,
                task: paaDetails,
                save: () => this.updatePaa('flowable'),
                rest: () => this.reset(),
                remove: () => {
                  this.paaDialogObject = getPaaDeleteObject({
                    store: this.paaRepository,
                    action: () => this.removePaa(),
                    toggleVisiblity: () => this.dialogComponent.toggleVisible(),
                  });
                  this.dialogComponent.toggleVisible();
                },
                validate: (value = 'true') => this.nextStep(paaDetails.taskId, value),
                reject: () => this.nextStep(paaDetails.taskId, 'false'),
                openDownloadDialog: () => this.validateAcStep('download'),
                openUploadDialog: () => this.validateAcStep('upload'),
                comment: this.comment,
                onCommentChange: (value: string) => this.onCommentChange(value),
                error: this.commentError,
              });

      const selectedAc = allAc.find(ac => ac.id === this.paaRepository.getNested('dto', 'acId'));
      this.acOptions = [{ name: selectedAc?.nom, code: selectedAc?.id }];

      this.acOptions = this.acOptions.concat(
        acWithNoPaa.map(ac => {
          return { name: ac.acName, code: ac.acId };
        })
      );
      this.formObject = getPaaUpdatePlanFormObject({
        store: this.paaRepository,
        acOptions: this.acOptions,
        download: (file: FileRepresentation) => this.fileService.download(file, NAMESPACE_ENUM.PAA),
        openFile: (file: FileRepresentation) => this.fileService.openFile(file, NAMESPACE_ENUM.PAA),
        loading: this.loading$,
        isUpdatable: this.isUpdatable$,
      });
      this.loading$.next(false);
    });

    this.paaMilestonesService.initialize();
  }

  downloadModel() {
    this.fileService.download(
      {
        filename: this.paaRepository.getNested('response', 'ref'),
        extension: 'pdf',
        alfresco_id: this.paaRepository.getNested('response', 'modelAlfrescoId'),
      },
      NAMESPACE_ENUM.PAA_MODEL
    );
  }

  nextStep(taskId?: string, value?: string | null) {
    const isUpdateMode = this.isUpdatable$.getValue();

    const formData = isUpdateMode
      ? this.paaRepository.getUpdatePayload(this.paaMilestonesService.getAllMilestoneDtos(), 'paa.').formData
      : new FormData();

    const commentValue = this.comment?.getValue();
    if (value === 'false' && !commentValue) {
      this.commentError.next('Veuillez saisir un commentaire');
      return;
    }

    if (value) formData.append('validate', value.toString());
    if (this.comment) formData.append('comment', commentValue);

    let isPayloadValid = true;
    let milestoneValid = true;

    if (isUpdateMode) {
      const year = this.paaRepository.getNested('dto', 'annee');
      milestoneValid = this.paaMilestonesService.validateAllMilestoneDtos('flowable', year ? new Date(Number(year), 0, 1) : undefined);
      const payload = this.paaRepository.getCreatePayload(this.paaMilestonesService.getAllMilestoneDtos());
      const result = paaPreDraftSchema.safeParse(payload.data);

      isPayloadValid = result.success;

      if (!isPayloadValid && result.error) {
        this.paaRepository.setErrors(result.error.flatten().fieldErrors);
        return;
      }
    }

    if (isPayloadValid && milestoneValid) {
      this.currentTasksService.validatePaa(Number(taskId), formData).subscribe({
        next: () => {
          this.comment.next('');
          this.multiFormAccordionService.reset(0);
          this.paaRepository.reset();
          url.reloadComponent(this.router);
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'Le paa a été passé pour validation avec succès',
          });
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || 'Une erreur est survenue lors de la passage du paa pour validation',
          });
        },
      });
    }
  }

  validateAcStep(variant: 'download' | 'upload') {
    if (variant === 'download')
      this.paaDialogObject = getPAADownloadDialog({
        store: this.paaRepository,
        validate: () => {
          this.paaService
            .downloadPaaModel({
              paaId: this.paaRepository.getNested('response', 'id'),
              paaVer: this.paaRepository.getNested('response', 'version'),
              signataire: this.paaRepository.getNested('download', 'signPerson'),
              fonction: this.paaRepository.getNested('download', 'function'),
            })
            .subscribe((blob: Blob) => {
              fileUtils.triggerBlobDownload(blob, `${this.paaDetails?.paa.ref}_modele.pdf`);
            });
          this.dialogComponent.toggleVisible();
        },
      });
    else
      this.paaDialogObject = getPAAUploadDialog({
        store: this.paaRepository,
        validate: () => {
          const modelPaaFileAsArray = this.paaRepository.getNested<File[]>('upload', 'modelPaaFile');
          if (modelPaaFileAsArray.length === 0) {
            this.messageService.add({
              severity: 'error',
              summary: 'Fichier manquant',
              detail: 'Veuillez sélectionner un fichier avant de passer pour validation',
            });
            return;
          }
          const formData = new FormData();
          const modelPaaFile = modelPaaFileAsArray[0];
          formData.append('validate', 'true');
          formData.append('comment', this.comment.getValue());
          formData.append('modelPaaFile', modelPaaFile);
          if (this.paaDetails?.taskId) {
            this.paaService.uploadPaaModel(this.paaDetails?.taskId, formData).subscribe({
              next: () => {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Succès',
                  detail: 'Le PAA a été passé pour validation avec succès',
                });
                this.dialogComponent.toggleVisible();
                url.reloadComponent(this.router);
              },
              error: err => {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Erreur',
                  detail: err.error.message || "Une erreur est survenue lors de la passage du PAA l'étape suivante",
                });
              },
            });
          }
        },
      });
    this.dialogComponent.toggleVisible();
  }

  startWorkflow() {
    this.paaService.startWorkflow(this.paaRepository.getNested('response', 'id')).subscribe({
      next: () => {
        this.multiFormAccordionService.reset(0);
        this.paaRepository.reset();
        url.reloadComponent(this.router);
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'Le paa a été passé pour validation avec succès',
        });
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || 'Une erreur est survenue lors de la passage du paa pour validation',
        });
      },
    });
  }

  updatePaa(mode: 'draft' | 'flowable', onSuccess?: () => void) {
    const year = this.paaRepository.getNested('dto', 'annee');
    const milestoneValidation = this.paaMilestonesService.validateAllMilestoneDtos(mode, year ? new Date(Number(year), 0, 1) : undefined);
    const payload = this.paaRepository.getUpdatePayload(this.paaMilestonesService.getAllMilestoneDtos());
    const result = paaPreDraftSchema.safeParse(payload.data);

    if (result.success && milestoneValidation) {
      this.loading$.next(true);
      this.paaService
        .updatePaa(this.paaRepository.getNested('response', 'id'), this.paaRepository.getNested('response', 'version'), payload.formData)
        .subscribe({
          next: () => {
            if (onSuccess) onSuccess();
            this.multiFormAccordionService.reset(0);
            this.paaRepository.reset();
            url.reloadComponent(this.router);
            this.messageService.add({
              severity: 'success',
              summary: 'Succès',
              detail: 'Le PAA a été mis à jour avec succès',
            });
            this.loading$.next(false);
          },
          error: err => {
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: err.error.message || 'Une erreur est survenue lors de la récupération de paa',
            });
            this.loading$.next(false);
          },
        });
    } else if (result.error) {
      this.paaRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  removePaa() {
    this.paaService.deletePaa(this.paaRepository.getNested('response', 'id'), this.paaRepository.getNested('response', 'version')).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'Le PAA a été supprimé avec succès',
        });
        this.dialogComponent.toggleVisible();
        this.paaRepository.reset();
        url.reloadComponent(this.router);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || 'Une erreur est survenue lors de la suppression du paa',
        });
        this.dialogComponent.toggleVisible();
      },
    });
  }

  reset() {
    this.paaRepository.reset();
    this.multiFormAccordionService.reset(0);
    if (this.acOptions.length === 1) {
      this.paaRepository.setNested('dto', 'acId', this.acOptions[0].code);
      this.paaRepository.set('acsSelected', this.acOptions[0]);
    }
  }
}
